/**
 * Role Play Collection Screen (Phase 4)
 *
 * Drill-down from RolePlayCollections — shows a single collection's
 * situations as a vertical list. Each situation is a tappable card
 * that navigates to the existing RolePlayTemplatePreview screen.
 *
 * Reads from the same store the catalog grid uses; if the collection
 * detail hasn't been loaded yet, fetches it on focus.
 */

import React, { useCallback, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useRolePlayStore,
  selectRolePlayCollectionBySlug,
  selectRolePlayTemplates,
  selectRolePlayCollectionsErrorInfo,
  selectLoadCollectionDetail,
  selectClearCollectionsError,
  type RolePlayTemplate,
} from '../store/rolePlayStore';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectActiveCourseId, selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../store/userPreferencesSelectors';
import { resolveBilingualContent, type BilingualContent } from '../types/lesson';

function useResolveBilingual() {
  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);
  return useCallback(
    (content: BilingualContent | string) =>
      resolveBilingualContent(content, lessonLanguage, sourceLocale, targetLocale),
    [lessonLanguage, sourceLocale, targetLocale],
  );
}

interface SituationRowProps {
  template: RolePlayTemplate;
  onPress: (id: string) => void;
}

const SituationRow = React.memo(function SituationRow({ template, onPress }: SituationRowProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(template.id), [onPress, template.id]);

  const title = resolve(template.title);
  const scenario = resolve(template.scenario);
  const difficulty = template.difficulty ?? 2;

  return (
    <TouchableOpacity
      testID={`roleplay-collection-template-${template.id}`}
      accessibilityRole="button"
      accessibilityLabel={t('roleplay.list.templateLabel', { title })}
      onPress={handlePress}
      activeOpacity={0.75}
    >
      <View style={[styles.row, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={{ flex: 1 }}>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
          >
            {title}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={2}
            style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 4, ...ffs }}
          >
            {scenario}
          </Text>
        </View>
        <View style={[styles.difficultyPill, { backgroundColor: theme.background, borderColor: theme.border }]}>
          <Text
            allowFontScaling={false}
            style={{ fontSize: 12 * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
          >
            {t('roleplay.collections.difficultyLabel', { level: difficulty })}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

export function RolePlayCollectionScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const slug = (route.params as { slug: string }).slug;

  const collection = useRolePlayStore(selectRolePlayCollectionBySlug(slug));
  // Select the STABLE `templates` array and filter in a memo. A selector that
  // returned `s.templates.filter(...)` handed `useSyncExternalStore` a fresh
  // array every render → "getSnapshot should be cached" → infinite re-render
  // → Maximum update depth exceeded (Zustand v5). `selectRolePlayTemplates`
  // returns the same reference until the array actually changes.
  const allTemplates = useRolePlayStore(selectRolePlayTemplates);
  const templates = useMemo(
    () => allTemplates.filter((t) => t.collectionSlug === slug),
    [allTemplates, slug],
  );
  const errorInfo = useRolePlayStore(selectRolePlayCollectionsErrorInfo);
  const loadDetail = useRolePlayStore(selectLoadCollectionDetail);
  const clearError = useRolePlayStore(selectClearCollectionsError);

  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);
  const resolve = useResolveBilingual();

  // Load detail on focus when we don't have templates for this slug yet.
  useFocusEffect(
    useCallback(() => {
      if (!activeCourseId) return;
      if (templates.length === 0) {
        void loadDetail(activeCourseId, slug);
      }
    }, [activeCourseId, slug, templates.length, loadDetail]),
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);
  const handleOpenTemplate = useCallback(
    (templateId: string) => navigation.navigate('RolePlayTemplatePreview', { templateId }),
    [navigation],
  );
  const handleRetry = useCallback(() => {
    clearError();
    if (activeCourseId) void loadDetail(activeCourseId, slug);
  }, [clearError, activeCourseId, slug, loadDetail]);

  const renderItem = useCallback(
    ({ item }: { item: RolePlayTemplate }) => (
      <SituationRow template={item} onPress={handleOpenTemplate} />
    ),
    [handleOpenTemplate],
  );
  const keyExtractor = useCallback((tpl: RolePlayTemplate) => tpl.id, []);

  const headerTitle = collection ? resolve(collection.title) : t('roleplay.list.title');
  const description = collection ? resolve(collection.description) : null;

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={headerTitle} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <View testID="roleplay-collection-screen" style={styles.scrollContent}>
          {description && (
            <Text
              allowFontScaling={false}
              style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginBottom: 12, ...ffs }}
            >
              {description}
            </Text>
          )}

          {errorInfo && (
            <View
              testID="roleplay-collection-error"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
            >
              <Text
                allowFontScaling={false}
                style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
              >
                {errorMessage(errorInfo)}
              </Text>
              <TouchableOpacity
                testID="roleplay-collection-error-retry"
                accessibilityRole="button"
                accessibilityLabel={t('roleplay.list.loadErrorRetry')}
                onPress={handleRetry}
                style={[styles.retryButton, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                >
                  {t('roleplay.list.loadErrorRetry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {!errorInfo && templates.length === 0 && (
            <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />
          )}

          {templates.length > 0 && (
            <FlatList
              data={templates}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              contentContainerStyle={{ gap: 8 }}
            />
          )}
        </View>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flex: 1,
    padding: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  difficultyPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
    marginBottom: 8,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
});
